import * as React from 'react';

import { Button, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { requestPhoneCode } from '@realadvisor/auth-client';
import { Form } from '@realadvisor/form';
import { useCaptcha } from '@realadvisor/hooks';
import { Box } from 'react-system';

import { reCaptchaKeyV2, reCaptchaKeyV3 } from '@legacy/config';
import {
  PhoneInput,
  getInitialPhonePrefix,
} from '@legacy/controls/phone-input';
import { useLocale } from '@legacy/hooks/locale';
import { formatPhoneNumberE164 } from '@modules/_shared/utils/phone';

const detectPhonePrefix = async (signal: AbortSignal) => {
  const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
    signal,
  });
  const body = await response.text();
  const countryCode = body.match(/\bloc=([\w]+)/)?.[1] ?? null;
  return getInitialPhonePrefix(countryCode);
};

export const collectMarketingMetadata = () => {
  const { href, searchParams } = new URL(window.location.href);
  return {
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
    landing_page_url: href,
    utm_source: searchParams.get('utm_source'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_medium: searchParams.get('utm_medium'),
  };
};

type Props = {
  onChange: (state: { number: string; tenantId: string | null }) => void;
  tenantId: string | null;
};

const DEFAULT_PREFIX = getInitialPhonePrefix(null);

const isCaptchaError = (message: string | undefined | null) =>
  message?.toLowerCase().includes('captcha') ?? false;

export const LoginPhoneForm = ({ onChange, tenantId }: Props) => {
  const { t } = useLocale();
  const [captchaSolved, setCaptchaSolved] = React.useState(false);
  const captcha = useCaptcha({
    reCaptchaKeyV2,
    reCaptchaKeyV3,
    onSolved: () => setCaptchaSolved(true),
  });
  const [requesting, setRequesting] = React.useState(false);
  const [number, setNumber] = React.useState(DEFAULT_PREFIX);
  const [error, setError] = React.useState<string | null>(null);
  const captchaShownRef = React.useRef(false);

  // Show captcha once when user starts typing
  React.useEffect(() => {
    if (number.length > DEFAULT_PREFIX.length && !captchaShownRef.current) {
      captcha.verifyUser();
      captchaShownRef.current = true;
    }
  }, [number, captcha]);

  // Detect country prefix on mount
  React.useEffect(() => {
    const controller = new AbortController();
    detectPhonePrefix(controller.signal)
      .then(setNumber)
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const handleError = (message: string | null | undefined) => {
    if (isCaptchaError(message)) {
      captcha.verifyUser();
    } else if (message === 'TOO_MANY_REQUESTS') {
      // Auth's rate-limit refusal is a machine token — don't show it raw.
      setError(t('Please wait a moment before requesting another code.'));
    } else {
      setError(message ?? 'Failed to request code');
    }
  };

  const submitForm = async () => {
    const normalizedNumber = formatPhoneNumberE164(number) ?? '';

    if (normalizedNumber === '') {
      setError(t('Invalid phone number'));
      return;
    }

    setRequesting(true);
    setError(null);

    captcha.requestToken(async recaptchaToken => {
      try {
        const response = await requestPhoneCode({
          number: normalizedNumber,
          tenantId,
          marketingMetadata: collectMarketingMetadata(),
          recaptchaToken,
        });

        if (response.ok) {
          const json = await response.json();
          if (json.hint != null) {
            alert(json.hint);
          }
          onChange({ number, tenantId });
        } else {
          const json = await response.json();
          handleError(json.error);
        }
      } catch (err) {
        handleError(err instanceof Error ? err.message : null);
      } finally {
        setRequesting(false);
      }
    });
  };

  return (
    <Form onSubmit={submitForm}>
      <Box py={1}>
        <FormControl error={error != null} disabled={requesting}>
          <InputLabel>{t('phoneNumber')}</InputLabel>
          <PhoneInput
            value={number}
            onChange={val => {
              setNumber(val);
              setError(null);
            }}
          />
          {error != null && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      </Box>
      {captcha.element}
      <Box py={1}>
        <Button
          fullWidth
          css={{ textAlign: 'center' }}
          variant="contained"
          color="secondary"
          disabled={error != null || requesting || !captchaSolved}
          onClick={submitForm}
        >
          {t('signInWithPhone')}
        </Button>
      </Box>
    </Form>
  );
};
